import { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Input,
  Textarea,
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardBody,
  CardFooter,
} from '@vnetwork/ui/primitives';
import { Field } from '@vnetwork/ui/forms';
import { useToast } from '@vnetwork/ui/feedback';
import { useMyContactDetail, useUpdateMyContact, type ContactUpdate } from '@/api/customer';
import { LANGUAGES, CUSTOMER_SOURCES, SERVICE_INTERESTS } from './contact-constants';
import styles from './Information.module.css';

const EMPTY: ContactUpdate = {
  contact_name: '',
  contact_phone: '',
  contact_address: '',
  contact_position: '',
  contact_industry: '',
  contact_note: '',
  contact_language: 'vi',
  contact_source: '',
  contact_service_interestings: [],
};

/** Account → Contact information. CRM contact details (legacy ManageContact). */
export function ContactInformationCard() {
  // Prefill from the SAME DataCentral contact the save writes to (not the
  // segment read-model) so edits persist visibly across reloads.
  const { data: detail } = useMyContactDetail();
  const { show } = useToast();
  const [form, setForm] = useState<ContactUpdate>(EMPTY);
  const [dirty, setDirty] = useState(false);

  const reset = useCallback(() => {
    if (detail) setForm({ ...EMPTY, ...detail });
    setDirty(false);
  }, [detail]);

  useEffect(() => {
    reset();
  }, [reset]);

  const update = useUpdateMyContact({
    onSuccess: () => {
      show({ tone: 'success', title: 'Contact information updated' });
      setDirty(false);
    },
    onError: () => show({ tone: 'danger', title: 'Could not update contact information' }),
  });

  function set<K extends keyof ContactUpdate>(key: K, value: ContactUpdate[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setDirty(true);
  }

  function toggleService(s: string) {
    const cur = form.contact_service_interestings ?? [];
    set('contact_service_interestings', cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]);
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    // Both are required by the backend (binding:"required"); tell the user which
    // is missing instead of silently doing nothing.
    if (!form.contact_name.trim() || !form.contact_phone.trim()) {
      show({
        tone: 'danger',
        title: 'Missing required fields',
        description: 'Full name and phone number are required.',
      });
      return;
    }
    // Reject obviously-invalid phone numbers (the field previously accepted any
    // text, e.g. "123456789"). Vietnamese format: a 10-digit number starting with
    // 0 (e.g. 0987654321); spaces/dashes are allowed and ignored.
    const digits = form.contact_phone.replace(/\D/g, '');
    if (!/^0\d{9}$/.test(digits)) {
      show({
        tone: 'danger',
        title: 'Invalid phone number',
        description: 'Enter a valid phone number, e.g. 0987654321.',
      });
      return;
    }
    update.mutate(form);
  }

  const selected = form.contact_service_interestings ?? [];

  return (
    <Card>
      <CardHeader>
        <CardTitle>Contact information</CardTitle>
        <CardDescription>How our team reaches you about your account.</CardDescription>
      </CardHeader>
      <CardBody>
        <form id="contact-form" onSubmit={submit} className={styles.contactForm}>
            <div className={styles.contactGrid}>
              <Field label="Full name" required>
                {(p) => <Input {...p} value={form.contact_name} onChange={(e) => set('contact_name', e.target.value)} />}
              </Field>
              <Field label="Phone" required>
                {(p) => <Input {...p} value={form.contact_phone} onChange={(e) => set('contact_phone', e.target.value)} />}
              </Field>
              <Field label="Position">
                {(p) => <Input {...p} value={form.contact_position} onChange={(e) => set('contact_position', e.target.value)} />}
              </Field>
              <Field label="Industry">
                {(p) => <Input {...p} value={form.contact_industry} onChange={(e) => set('contact_industry', e.target.value)} />}
              </Field>
              <Field label="Language">
                {(p) => (
                  <Select value={form.contact_language} onValueChange={(v) => set('contact_language', v)}>
                    <SelectTrigger {...p}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => (
                        <SelectItem key={l.value} value={l.value}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
              <Field label="Source">
                {(p) => (
                  <Select value={form.contact_source || undefined} onValueChange={(v) => set('contact_source', v)}>
                    <SelectTrigger {...p}>
                      <SelectValue placeholder="Select a source" />
                    </SelectTrigger>
                    <SelectContent>
                      {CUSTOMER_SOURCES.map((s) => (
                        <SelectItem key={s.value} value={s.value}>
                          {s.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </Field>
            </div>

            <Field label="Address">
              {(p) => <Input {...p} value={form.contact_address} onChange={(e) => set('contact_address', e.target.value)} />}
            </Field>
            <Field label="Note">
              {(p) => (
                <Textarea {...p} rows={3} value={form.contact_note} onChange={(e) => set('contact_note', e.target.value)} />
              )}
            </Field>
            <Field label="Services of interest">
              {() => (
                <div className={styles.chipRow}>
                  {SERVICE_INTERESTS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      className={selected.includes(s) ? `${styles.chip} ${styles.chipOn}` : styles.chip}
                      onClick={() => toggleService(s)}
                      aria-pressed={selected.includes(s)}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              )}
            </Field>
        </form>
      </CardBody>
      <CardFooter>
        <Button type="button" variant="secondary" size="sm" disabled={!dirty || update.isPending} onClick={reset}>
          Cancel
        </Button>
        <Button type="submit" form="contact-form" size="sm" disabled={!dirty || update.isPending}>
          {update.isPending ? 'Saving…' : 'Save changes'}
        </Button>
      </CardFooter>
    </Card>
  );
}
